/**
 * PipelineList — renders saved pipeline entries extracted from ToolSidebar.
 */

import { Pencil, Trash2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { ISavedPipeline } from '@/interfaces/IStorage';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PipelineListProps {
  pipelines: ISavedPipeline[];
  onOpen: (pipeline: ISavedPipeline) => void;
  onEdit: (pipeline: ISavedPipeline) => void;
  onDelete: (pipeline: ISavedPipeline) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PipelineList: React.FC<PipelineListProps> = ({ pipelines, onOpen, onEdit, onDelete }) => {
  if (pipelines.length === 0) {
    return <div className="text-sm text-muted-foreground px-2 py-2">No saved pipelines.</div>;
  }

  return (
    <>
      {pipelines.map((p) => (
        <div key={p.id} className="w-full rounded-md px-2 py-2 hover:bg-accent/60 flex items-start gap-2">
          <button onClick={() => onOpen(p)} className="flex-1 min-w-0 text-left" title={p.description ?? p.name}>
            <div className="text-sm font-medium truncate">{p.name}</div>
            <div className="text-xs text-muted-foreground truncate">{p.steps.length} steps</div>
          </button>

          <div className="flex items-center gap-1 flex-shrink-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 px-0"
                    aria-label="Edit pipeline"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(p); }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Edit</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 px-0"
                    aria-label="Delete pipeline"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(p); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Delete</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      ))}
    </>
  );
};
